import { Children, isValidElement, ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router';
import { NavLinkProps } from 'react-router-dom';
import { Flex, FlexProps } from '@chakra-ui/react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import SunshineIcon, { IconName } from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import { logEvent } from 'features/Analytics/analytics';
import { useIsBrowsingDisabled } from 'layouts/AppLayout/context';

import { useIsLinkActive } from '../hooks/useIsLinkActive';
import { AnimatedLinkContainer } from './AnimatedLinkContainer';
import { NavigationEntryProps } from './NavigationEntry';
import { NavigationHighlight } from './NavigationHighlight';

interface NavigationSectionViewProps extends Omit<FlexProps, 'title'> {
  /**
   * Displayed title of the section
   */
  title: ReactNode;
  /**
   * Displayed icon of the section
   */
  icon: IconName;
  /**
   * An array of NavigationEntry components
   */
  children?: ReactNode;
  /**
   * Path to the root of the section. This is used to determine if the links defined under are active & also to navigate.
   */
  sectionRoot: NavLinkProps['to'];
}

function NavigationSectionView({
  children,
  icon,
  onClick,
  sectionRoot,
  title,
  ...props
}: NavigationSectionViewProps) {
  const hasSubItems = !!children;
  const isBrowsingDisabled = useIsBrowsingDisabled();
  const isActive = useIsLinkActive(sectionRoot, hasSubItems);
  const { companyProfileId, companyUserId } = useCompanyContext();
  const location = useLocation();

  // Based on the state of its children, the section knows when to toggle, and when to display a highlight.
  // This is done by checking if any of the children is highlighted or if any of the children route matches partially the curent location.
  const { isHighlighted, isInCurrentSection } = useMemo(() => {
    const subRoutesProps =
      Children.map(children, (child) => {
        if (isValidElement<NavigationEntryProps>(child)) {
          return {
            isHighlighted: child.props.isHighlighted,
            path: child.props.to,
          };
        } else {
          return null;
        }
      })?.filter(Boolean) || [];

    return {
      isHighlighted: subRoutesProps.some((route) => route.isHighlighted),
      isInCurrentSection: subRoutesProps.some((route) =>
        location.pathname.includes(route.path.toString()),
      ),
    };
  }, [children, location.pathname]);

  return (
    <Flex flexDirection="column">
      <AnimatedLinkContainer
        alignItems="center"
        disabled={isBrowsingDisabled}
        flexDirection="row"
        isActive={isActive}
        onClick={(e) => {
          logEvent({
            name: 'Click_Menu_Link',
            properties: {
              companyProfileId,
              companyUserId,
              to: sectionRoot.toString(),
            },
          });
          onClick?.(e);
        }}
        paddingLeft="space-16"
        to={sectionRoot}
        {...props}
      >
        <SunshineIcon marginRight="space-12" name={icon} />
        <Flex alignItems="center" direction="row" gap="space-4">
          <Text
            alignItems="center"
            display="flex"
            variant={isActive ? 'primary' : 'secondary'}
          >
            {title}
          </Text>
          {isHighlighted && !isInCurrentSection ? (
            <NavigationHighlight />
          ) : null}
          {hasSubItems ? (
            <SunshineIcon
              marginLeft="space-4"
              name="chevron-down"
              transform={isInCurrentSection ? 'rotate(180deg)' : ''}
            />
          ) : null}
        </Flex>
      </AnimatedLinkContainer>

      <Flex
        as="ul"
        display={
          isInCurrentSection || (isActive && hasSubItems) ? 'block' : 'none'
        }
        flexDirection="column"
      >
        {children}
      </Flex>
    </Flex>
  );
}

export interface NavigationSectionProps extends NavigationSectionViewProps {
  isVisible?: boolean;
}

export function NavigationSection({
  isVisible = true,
  ...props
}: NavigationSectionProps) {
  if (!isVisible) {
    return null;
  }

  return <NavigationSectionView {...props} />;
}
